// Removing a worker, and saying what that does and does not reach.
//
// Three shapes, because "remove" means three different amounts of work. A worker
// this manager runs as a container comes off the docker socket. A worker on
// another machine with a saved key can be taken down over SSH, if asked. A worker
// on another machine without one can only be forgotten — its containers keep
// running there, and the dialog has to say so rather than implying a clean
// removal it cannot perform.

import { Checkbox } from "@veil/ui/components/checkbox";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
} from "@veil/ui/components/field";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import TriangleAlert from "lucide-react/dist/esm/icons/triangle-alert";
import { useState } from "react";
import type { WorkerRow } from "@/lib/workers/types";
import {
	canReachRemoteHost,
	useWorkerDelete,
} from "@/lib/workers/use-worker-lifecycle";

export function DeleteWorkerDialog({
	worker,
	onOpenChange,
}: {
	worker: WorkerRow | null;
	onOpenChange: (open: boolean) => void;
}) {
	// Deleting the install volume destroys the multi-GB chromium build, the
	// profile bundles and sessions.db, so it starts off. Uninstalling starts off
	// too, for a stronger reason: it acts on a machine that is not this one.
	const [removeVolume, setRemoveVolume] = useState(false);
	const [uninstall, setUninstall] = useState(false);
	const close = () => {
		setRemoveVolume(false);
		setUninstall(false);
		onOpenChange(false);
	};
	const del = useWorkerDelete({ onDeleted: close });
	const managed = worker?.managed ?? false;
	// What the server calls a remote worker in this same path: no container name
	// means there is nothing here to stop, so whatever is running is elsewhere.
	const remote = worker ? worker.containerName === null : false;
	const canUninstall = worker ? canReachRemoteHost(worker) : false;

	return (
		<ResponsiveDialog
			variant="alert"
			open={!!worker}
			onOpenChange={(open) => {
				if (!open) close();
			}}
		>
			<ResponsiveDialogContent>
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>
						Remove <span className="font-mono">{worker?.hostname}</span>?
					</ResponsiveDialogTitle>
					<ResponsiveDialogDescription>
						{managed
							? "This drops the worker from the upstream pool and removes its container. Polling history is kept."
							: "This drops the worker from the upstream pool and re-renders nginx straight away, so nothing routes to it. Polling history for this worker is kept."}
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>

				{managed ? (
					<Field orientation="horizontal" className="rounded-md border p-3">
						<Checkbox
							id="del-volume"
							checked={removeVolume}
							onCheckedChange={(v) => setRemoveVolume(v === true)}
							disabled={del.isPending}
						/>
						<FieldContent>
							<FieldLabel htmlFor="del-volume">
								Also delete this worker's data
							</FieldLabel>
							<FieldDescription>
								Removes the install volume: the downloaded browser builds,
								profile bundles and session database. Leave unchecked to keep
								them for a future worker of the same name.
							</FieldDescription>
						</FieldContent>
					</Field>
				) : null}

				{remote && canUninstall ? (
					<div className="rounded-md border">
						<Field orientation="horizontal" className="p-3">
							<Checkbox
								id="del-uninstall"
								checked={uninstall}
								onCheckedChange={(v) => setUninstall(v === true)}
								disabled={del.isPending}
							/>
							<FieldContent>
								<FieldLabel htmlFor="del-uninstall">
									Also uninstall from the remote host
								</FieldLabel>
								<FieldDescription>
									Opens <span className="font-mono">{worker?.publicIp}</span>{" "}
									with this worker's saved key, takes its stack down and removes
									its directory. Leave unchecked and the record goes away while
									the containers keep running there.
								</FieldDescription>
							</FieldContent>
						</Field>
						{uninstall ? (
							<Field orientation="horizontal" className="border-t p-3 sm:pl-9">
								<Checkbox
									id="del-volume-remote"
									checked={removeVolume}
									onCheckedChange={(v) => setRemoveVolume(v === true)}
									disabled={del.isPending}
								/>
								<FieldContent>
									<FieldLabel htmlFor="del-volume-remote">
										Also delete this worker's data
									</FieldLabel>
									<FieldDescription>
										Removes the install volume on that host too: the downloaded
										browser builds, profile bundles and session database. Leave
										unchecked and a worker deployed there again keeps them.
									</FieldDescription>
								</FieldContent>
							</Field>
						) : null}
					</div>
				) : null}

				{remote && !canUninstall ? (
					<div className="flex gap-2 rounded-md border p-3 text-sm">
						<TriangleAlert
							className="mt-0.5 size-4 shrink-0"
							aria-hidden="true"
						/>
						<div className="space-y-1">
							<p className="font-medium">Its containers will be left running</p>
							<p className="text-muted-foreground">
								{worker?.hostname} runs on{" "}
								<span className="font-mono">{worker?.publicIp}</span>, and this
								manager has no saved SSH key for it — so removing the record
								here cannot stop anything there. Take the stack down on that
								machine yourself, or the containers stay up with nothing in this
								console listing them.
							</p>
						</div>
					</div>
				) : null}

				<ResponsiveDialogFooter>
					<ResponsiveDialogCancel disabled={del.isPending}>
						Cancel
					</ResponsiveDialogCancel>
					<ResponsiveDialogAction
						variant="destructive"
						disabled={del.isPending}
						onClick={(e) => {
							e.preventDefault();
							if (!worker) return;
							del.mutate({
								hostname: worker.hostname,
								removeContainer: managed,
								removeVolume: (managed || uninstall) && removeVolume,
								uninstall: canUninstall && uninstall,
							});
						}}
					>
						{del.isPending
							? "Removing…"
							: uninstall
								? "Remove and uninstall"
								: "Remove worker"}
					</ResponsiveDialogAction>
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
